/**
 * Platform Operations - RTK Query API
 *
 * OPS-01: wires BackupManagement.jsx to the real, staff-only
 * apps.platform_ops.views.BackupRunViewSet (/api/v1/platform-ops/backups/).
 * That viewset is intentionally read-only plus two actions: `trigger`
 * (enqueue a manual backup) and `download` (stream a decrypted backup).
 * There is no self-service restore endpoint -- by design, per the
 * viewset's own module docstring -- so no restore mutation exists here.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export const platformOpsApi = createApi({
  reducerPath: 'platformOpsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BackupRun'],
  endpoints: (builder) => ({
    /**
     * List backup runs (most recent first, per the model's default
     * ordering), staff/superuser only.
     */
    getBackupRuns: builder.query({
      query: () => ({
        url: '/api/v1/platform-ops/backups/',
      }),
      transformResponse: (response) =>
        Array.isArray(response) ? response : response?.results || [],
      providesTags: ['BackupRun'],
    }),

    /**
     * Enqueue a manual backup (`run_database_backup.delay(...)`).
     * The backend has exactly one global manual-trigger action -- there
     * is no per-schedule/per-type trigger parameter.
     */
    triggerBackup: builder.mutation({
      query: () => ({
        url: '/api/v1/platform-ops/backups/trigger/',
        method: 'POST',
      }),
      invalidatesTags: ['BackupRun'],
    }),
  }),
});

export const { useGetBackupRunsQuery, useTriggerBackupMutation } = platformOpsApi;

export default platformOpsApi;
